import { Box, Dialog, Typography } from "@mui/material";
import { useCallback, useState } from "react";
import styles from "./importBudgets.module.scss";
import {
  CommonButton,
  CommonOutlinedButton,
  CustomFilecon,
  CustomRequiredInputLabel,
} from "../../common/StyledComponents/StyledFields";
import tablestyles from "../ViewRequests/ViewRequests.module.scss";
import { Close, InsertDriveFileOutlined } from "@mui/icons-material";
import { useFormik } from "formik";
import fileUpload from "../../../assets/FileUpload.svg";
import commonstyles from "../ExpenseRequest/ExpenseRequest.module.scss";
import { ValidationError, object, string } from "yup";
import {
  apiRequest,
  catchErrorMessageHandler,
  displayDateFormat,
} from "../../../infrastructure/backendService";
import SuccessPopup from "../../devotee/donation/successPopup";
import toast from "react-hot-toast";
import LOCALE from "../../../config/en-us.json";
import ErrorPopup from "../../common/ErrorPopup";
import constants from "../../../config/constants.json";
import CustomDatePickerField from "../../common/DatePicker/DatePicker";
import deleteIcon from "../../../assets/DeleteIcon.svg";

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB in byte
interface ErrorMessageTypes {
  basicdate: string;
  file: string;
  fileName: string;
}
const ImportBudgets = (props: any) => {
  const { handleUploadReceipt } = props;
  const [open, setOpen] = useState(false);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [uploadedFileName, setUploadedFileName] = useState<string | null>(null);
  const initialValues = {
    basicdate: "",
    fileName: "",
    file: "",
  };
  const [formData, setFormData] = useState({
    ...initialValues,
  });
  const [errorMessage, setErrorMessage] = useState<ErrorMessageTypes>({
    ...initialValues,
  });
  const handleFieldChange = (field: any, value: any): any => {
    setFormData({
      ...formData,
      [field]: value,
    });
    setErrorMessage({ ...errorMessage, basicdate: "" });
  };

  const validationSchema = object().shape({
    basicdate: string().required("Date is required"),
    file: string(),
  });
  const handleSave = async (): Promise<void> => {
    const errors: ErrorMessageTypes = { ...initialValues };
    try {
      // Validate the form data against the validation schema
      await validationSchema.validate(formData, { abortEarly: false });
      // If validation passes, you can submit the form
      setErrorMessage(errors);
      // Call your API or perform other actions here
      saveApiCall();
    } catch (error: any) {
      // Handle validation errors
      if (error instanceof ValidationError) {
        error.inner.forEach((e: any) => {
          if (e.path) {
            errors[e.path as keyof ErrorMessageTypes] = e.message;
          }
        });
      }
      setErrorMessage(errors);
    }
  };

  const saveApiCall = () => {
    const year =
      displayDateFormat(new Date(formData.basicdate)).slice(-4) || "";
    const payload = new FormData();
    payload.append("file", formik?.values?.file);
    apiRequest({
      method: "POST",
      endpoint: `/temple/accountsBudget/years/${year}/csv`,
      body: payload,
      includeToken: true,
      headers: {},
    })
      .then((res) => {
        const successMessage = `${LOCALE.IMPORT_BUDGET}  ${uploadedFileName}`;
        toast.custom(
          <SuccessPopup
            headerMessage="Import"
            showMessage={successMessage}
            isOpen={isOpen}
            setIsOpen={setIsOpen}
          />
        );
        setFormData(initialValues);
        setUploadedFileName("");
      })
      .catch((err) => {
        toast.custom(
          <ErrorPopup
            showMessage={catchErrorMessageHandler(err)}
            isOpen={isOpen}
            setIsOpen={setIsOpen}
          />,
          {
            duration: constants.TOAST_POPUP_DURATION,
          }
        );
      });
  };

  const handleDrop = useCallback((event: any) => {
    event.preventDefault();
    const files: any = Array.from(event.dataTransfer?.files);

    if (files?.length > 0) {
      const file = files[0];
      // Check if the file type is CSV
      if (file.type === "text/csv" || file.name.endsWith(".csv")) {
        if (file.size <= MAX_FILE_SIZE) {
          formik.setFieldValue("file", file);
          formik.setFieldValue("fileName", file.name);
          setErrorMessage({ ...errorMessage, file: "" });
        } else {
          // File exceeds the size limit
          setErrorMessage({
            ...errorMessage,
            file: "The file size should not exceed 10MB",
          });
        }
      } else {
        // File is not CSV, handle the error or ignore
        setErrorMessage({
          ...errorMessage,
          file: "Please upload only CSV files",
        });
      }
    }
  }, []);

  const handleDragOver = useCallback((event: any) => {
    event.preventDefault();
  }, []);

  const formik = useFormik({
    initialValues,
    onSubmit(values) {
      handleUploadReceipt(values);
      formik.resetForm();
    },
    enableReinitialize: true,
  });

  const handleUpload = () => {
    setOpen(false);
    setUploadedFileName(formik?.values?.fileName);
    setErrorMessage({ ...errorMessage, file: "" });
  };
  const handleFileChange = (event: any) => {
    const files: any = Array.from(event.target.files);
    if (files?.length > 0) {
      const file = files[0];
      // Check if the file type is CSV
      if (file.type === "text/csv" || file.name.endsWith(".csv")) {
        if (file.size <= MAX_FILE_SIZE) {
          formik.setFieldValue("file", file);
          formik.setFieldValue("fileName", file.name);
          setErrorMessage({ ...errorMessage, file: "" });
        } else {
          // File exceeds the size limit
          setErrorMessage({
            ...errorMessage,
            file: "The file size should not exceed 10MB",
          });
        }
      } else {
        // File is not CSV, handle the error or ignore
        setErrorMessage({
          ...errorMessage,
          file: "Please upload only CSV files",
        });
      }
    }
  };

  const handleSelectFile = () => {
    document?.getElementById("file-input")?.click();
  };
  const handleRemoveFile = () => {
    formik.setFieldValue("file", null);
    formik.setFieldValue("fileName", "");
    setUploadedFileName("");
  };

  const handleClose = () => {
    setOpen(false);
  };
  const dailogOpen = () => {
    setOpen(true);
    setErrorMessage({ ...errorMessage, file: "" });
    formik.setFieldValue("file", null);
    formik.setFieldValue("fileName", "");
  };

  const renderAttachBudgetTable = () => {
    return (
      <Box sx={{ overflowY: "auto", mt: "15px" }}>
        <table className={tablestyles.table_container}>
          <thead>
            <tr>
              <th>Attach file</th>
              <th>Details</th>
              {uploadedFileName && <th>Action</th>}
            </tr>
          </thead>
          <tbody id="searchData">
            <tr>
              <td>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    color: "#F05802",
                  }}
                >
                  <CustomFilecon />
                  {uploadedFileName ? uploadedFileName : "Click on attach file"}
                </Box>
              </td>
              <td>
                File should be comma separated and in the following format
                Account Number, Account Budget, Account Description,....
              </td>
              {uploadedFileName && (
                <td>
                  <Box sx={{ textAlign: "center" }}>
                    <Box
                      component={"img"}
                      src={deleteIcon}
                      alt="delete"
                      onClick={() => handleRemoveFile()}
                      sx={{ cursor: "pointer" }}
                    />
                  </Box>
                </td>
              )}
            </tr>
          </tbody>
        </table>
      </Box>
    );
  };

  const BasicDatePicker = () => {
    return (
      <CustomDatePickerField
        date={formData.basicdate}
        handleDateChange={handleFieldChange}
        errorMessage={errorMessage.basicdate}
        name="basicdate"
      />
    );
  };

  const renderFieldsComponent = () => {
    return (
      <>
        <Box
          sx={{
            mt: "10px",
            p: "15px",
            border: "1px dashed black",
            borderRadius: "6px",
            textAlign: "center",
          }}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          <input
            type="file"
            id="file-input"
            accept=".csv"
            style={{ display: "none" }}
            onChange={handleFileChange}
            name="file"
          />
          <Box
            component={"img"}
            src={fileUpload}
            alt="Upload file"
            sx={{ width: "35px" }}
          />
          <Typography className={styles.drag_drop_txt}>
            Drag and Drop here (Only .csv)
          </Typography>
          <Typography className={styles.or_text}>or</Typography>
          <CommonButton onClick={handleSelectFile}>Select File</CommonButton>
        </Box>
        {formik.values.fileName && (
          <Box
            sx={{
              mt: "10px",
              background: "#7EAAFF38",
              p: "10px",
              display: "flex",
              alignItems: "center",
            }}
          >
            <InsertDriveFileOutlined sx={{ mr: "5px", fontSize: "32px" }} />
            <Box sx={{ width: "100%" }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  width: "100%",
                  mb: "4px",
                }}
              >
                <Typography className={styles.file_name}>
                  {formik.values.fileName}
                </Typography>
                <img
                  src={deleteIcon}
                  alt="delete item"
                  style={{ cursor: "pointer" }}
                  onClick={handleRemoveFile}
                />
              </Box>
              <Box
                sx={{
                  width: "100%",
                  background: "#22A699",
                  height: "5px",
                  borderRadius: "2.5px",
                }}
              />
            </Box>
          </Box>
        )}
        {errorMessage.file && (
          <Typography sx={{ color: "red", fontSize: "12px" }}>
            {errorMessage.file}
          </Typography>
        )}
      </>
    );
  };

  const renderButtonsComponent = () => {
    return (
      <Box sx={{ mt: "20px" }}>
        <CommonButton
          sx={{ mr: "10px" }}
          onClick={handleUpload}
          disabled={!formik?.values?.file}
        >
          Upload
        </CommonButton>
        <CommonOutlinedButton onClick={handleClose}>
          Cancel
        </CommonOutlinedButton>
      </Box>
    );
  };

  const renderAttachBudgetDialog = () => {
    return (
      <Dialog
        onClose={handleClose}
        open={open}
        PaperProps={{
          sx: {
            width: { xs: "100%", md: "600px" },
            maxWidth: { xs: "100%", sm: "600px" },
            margin: { xs: "10px", sm: "20px" },
          },
        }}
      >
        <Box
          className={commonstyles.add_vendor_main_container}
          sx={{
            width: "100%",
          }}
        >
          <Box className={commonstyles.dialog_title_container}>
            <Typography className={commonstyles.title}>Add file</Typography>
            <Close
              sx={{ color: "white", cursor: "pointer" }}
              onClick={handleClose}
            />
          </Box>
          <Box className={commonstyles.dialog_inner_container}>
            {renderFieldsComponent()}
            {renderButtonsComponent()}
          </Box>
        </Box>
      </Dialog>
    );
  };

  return (
    <Box className={styles.totalLayout}>
      <Typography className={"sub_heading"}>Import Budgets</Typography>
      <Box className={"common_box_shadow"} sx={{ mt: "20px" }}>
        <Box sx={{ maxWidth: "320px" }}>
          {CustomRequiredInputLabel("Date", true)}
          {BasicDatePicker()}
        </Box>
      </Box>
      {renderAttachBudgetTable()}
      <CommonButton
        sx={{ mt: "15px" }}
        onClick={() => (uploadedFileName ? handleSave() : dailogOpen())}
      >
        {uploadedFileName ? "Import" : "Attach File"}
      </CommonButton>
      {renderAttachBudgetDialog()}
    </Box>
  );
};
export default ImportBudgets;
